import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import marked_1 from 'vscode/external/vscode-marked/lib/marked.esm.js';
import { IBulkEditService } from 'vscode/vscode/vs/editor/browser/services/bulkEditService';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { registerAction2, Action2, MenuId, MenuRegistry } from 'vscode/vscode/vs/platform/actions/common/actions';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { ResourceNotebookCellEdit } from 'vscode/vscode/vs/workbench/contrib/bulkEdit/browser/bulkCellEdits';
import { CHAT_CATEGORY } from 'vscode/vscode/vs/workbench/contrib/chat/browser/actions/chatActions';
import { IChatWidgetService } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chat.service';
import { ChatAgentLocation } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents';
import { CONTEXT_RESPONSE_VOTE, CONTEXT_RESPONSE, CONTEXT_CHAT_RESPONSE_SUPPORT_ISSUE_REPORTING, CONTEXT_RESPONSE_FILTERED, CONTEXT_IN_CHAT_SESSION, CONTEXT_IN_CHAT_INPUT, CONTEXT_REQUEST, CONTEXT_CHAT_LOCATION, CONTEXT_RESPONSE_DETECTED_AGENT_COMMAND } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatContextKeys';
import { InteractiveSessionVoteDirection } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatService';
import { IChatService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatService.service';
import { isResponseVM, isRequestVM } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatViewModel';
import { NOTEBOOK_EDITOR_ID, CellKind } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';
import { NOTEBOOK_IS_ACTIVE_EDITOR } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookContextKeys';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';

const _moduleId = "vs/workbench/contrib/chat/browser/actions/chatTitleActions";
function registerChatTitleActions() {
    registerAction2(class MarkHelpfulAction extends Action2 {
        constructor() {
            super({
                id: 'workbench.action.chat.markHelpful',
                title: ( localize2WithPath(_moduleId, 0, "Helpful")),
                f1: false,
                category: CHAT_CATEGORY,
                icon: Codicon.thumbsup,
                toggled: ( (CONTEXT_RESPONSE_VOTE.isEqualTo('up'))),
                menu: {
                    id: MenuId.ChatMessageTitle,
                    group: 'navigation',
                    order: 1,
                    when: CONTEXT_RESPONSE
                }
            });
        }
        run(accessor, ...args) {
            const item = args[0];
            if (!isResponseVM(item)) {
                return;
            }
            const chatService = accessor.get(IChatService);
            chatService.notifyUserAction({
                agentId: item.agent?.id,
                sessionId: item.sessionId,
                requestId: item.requestId,
                result: item.result,
                action: {
                    kind: 'vote',
                    direction: InteractiveSessionVoteDirection.Up,
                }
            });
            item.setVote(InteractiveSessionVoteDirection.Up);
        }
    });
    registerAction2(class MarkUnhelpfulAction extends Action2 {
        constructor() {
            super({
                id: 'workbench.action.chat.markUnhelpful',
                title: ( localize2WithPath(_moduleId, 1, "Unhelpful")),
                f1: false,
                category: CHAT_CATEGORY,
                icon: Codicon.thumbsdown,
                toggled: ( (CONTEXT_RESPONSE_VOTE.isEqualTo('down'))),
                menu: {
                    id: MenuId.ChatMessageTitle,
                    group: 'navigation',
                    order: 2,
                    when: CONTEXT_RESPONSE
                }
            });
        }
        run(accessor, ...args) {
            const item = args[0];
            if (!isResponseVM(item)) {
                return;
            }
            const chatService = accessor.get(IChatService);
            chatService.notifyUserAction({
                agentId: item.agent?.id,
                sessionId: item.sessionId,
                requestId: item.requestId,
                result: item.result,
                action: {
                    kind: 'vote',
                    direction: InteractiveSessionVoteDirection.Down,
                }
            });
            item.setVote(InteractiveSessionVoteDirection.Down);
        }
    });
    registerAction2(class ReportIssueForBugAction extends Action2 {
        constructor() {
            super({
                id: 'workbench.action.chat.reportIssueForBug',
                title: ( localize2WithPath(_moduleId, 2, "Report Issue")),
                f1: false,
                category: CHAT_CATEGORY,
                icon: Codicon.report,
                menu: {
                    id: MenuId.ChatMessageTitle,
                    group: 'navigation',
                    order: 3,
                    when: ( (ContextKeyExpr.and(CONTEXT_CHAT_RESPONSE_SUPPORT_ISSUE_REPORTING, CONTEXT_RESPONSE)))
                }
            });
        }
        run(accessor, ...args) {
            const item = args[0];
            if (!isResponseVM(item)) {
                return;
            }
            const chatService = accessor.get(IChatService);
            chatService.notifyUserAction({
                agentId: item.agent?.id,
                sessionId: item.sessionId,
                requestId: item.requestId,
                result: item.result,
                action: {
                    kind: 'bug'
                }
            });
        }
    });
    registerAction2(class InsertToNotebookAction extends Action2 {
        constructor() {
            super({
                id: 'workbench.action.chat.insertIntoNotebook',
                title: ( localize2WithPath(_moduleId, 3, "Insert into Notebook")),
                f1: false,
                category: CHAT_CATEGORY,
                icon: Codicon.insert,
                menu: {
                    id: MenuId.ChatMessageTitle,
                    group: 'navigation',
                    isHiddenByDefault: true,
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_IS_ACTIVE_EDITOR,
                        CONTEXT_RESPONSE,
                         (CONTEXT_RESPONSE_FILTERED.negate())
                    )))
                }
            });
        }
        async run(accessor, ...args) {
            const item = args[0];
            if (!isResponseVM(item)) {
                return;
            }
            const editorService = accessor.get(IEditorService);
            if (editorService.activeEditorPane?.getId() === NOTEBOOK_EDITOR_ID) {
                const notebookEditor = editorService.activeEditorPane.getControl();
                if (!notebookEditor.hasModel()) {
                    return;
                }
                if (notebookEditor.isReadOnly) {
                    return;
                }
                const value = item.response.asString();
                const splitContents = splitMarkdownAndCodeBlocks(value);
                const focusRange = notebookEditor.getFocus();
                const index = Math.max(focusRange.end, 0);
                const bulkEditService = accessor.get(IBulkEditService);
                await bulkEditService.apply([
                    ( (new ResourceNotebookCellEdit(notebookEditor.textModel.uri, {
                        editType: 1 ,
                        index: index,
                        count: 0,
                        cells: ( (splitContents.map(content => {
                            const kind = content.type === 'markdown' ? CellKind.Markup : CellKind.Code;
                            const language = content.type === 'markdown' ? 'markdown' : content.language;
                            const mime = content.type === 'markdown' ? 'text/markdown' : `text/x-${content.language}`;
                            return {
                                cellKind: kind,
                                language,
                                mime,
                                source: content.content,
                                outputs: [],
                                metadata: {}
                            };
                        })))
                    })))
                ], { quotableLabel: 'Insert into Notebook' });
            }
        }
    });
    registerAction2(class RemoveAction extends Action2 {
        constructor() {
            super({
                id: 'workbench.action.chat.remove',
                title: ( localize2WithPath(_moduleId, 4, "Remove Request and Response")),
                f1: false,
                category: CHAT_CATEGORY,
                icon: Codicon.x,
                keybinding: {
                    primary: 20 ,
                    mac: {
                        primary: 2048  | 1 ,
                    },
                    when: ( (ContextKeyExpr.and(CONTEXT_IN_CHAT_SESSION,  (CONTEXT_IN_CHAT_INPUT.negate())))),
                    weight: 200 ,
                },
                menu: {
                    id: MenuId.ChatMessageTitle,
                    group: 'navigation',
                    order: 2,
                    when: CONTEXT_REQUEST
                }
            });
        }
        run(accessor, ...args) {
            let item = args[0];
            if (!isRequestVM(item)) {
                const chatWidgetService = accessor.get(IChatWidgetService);
                const widget = chatWidgetService.lastFocusedWidget;
                item = widget?.getFocus();
            }
            const requestId = isRequestVM(item) ? item.id :
                isResponseVM(item) ? item.requestId : undefined;
            if (requestId) {
                const chatService = accessor.get(IChatService);
                chatService.removeRequest(item.sessionId, requestId);
            }
        }
    });
    const rerunMenu = MenuId.for('ChatMessageTitle#Rerun');
    MenuRegistry.appendMenuItem(MenuId.ChatMessageTitle, {
        submenu: rerunMenu,
        title: ( localizeWithPath(_moduleId, 5, "Rerun...")),
        icon: Codicon.refresh,
        group: 'navigation',
        order: -10,
        when: ( (ContextKeyExpr.and(
            CONTEXT_RESPONSE,
             (CONTEXT_CHAT_LOCATION.isEqualTo(ChatAgentLocation.Editor))
        )))
    });
    registerAction2(class RerunAction extends Action2 {
        constructor() {
            super({
                id: 'workbench.action.chat.rerun',
                title: ( localize2WithPath(_moduleId, 6, "Rerun Request")),
                f1: false,
                category: CHAT_CATEGORY,
                icon: Codicon.refresh,
                precondition: ( (CONTEXT_CHAT_LOCATION.isEqualTo(ChatAgentLocation.Editor))),
                menu: {
                    id: rerunMenu,
                    group: 'navigation',
                    order: -1,
                }
            });
        }
        async run(accessor, ...args) {
            const chatWidgetService = accessor.get(IChatWidgetService);
            const chatService = accessor.get(IChatService);
            const widget = chatWidgetService.lastFocusedWidget;
            let item = args[0];
            if (!isResponseVM(item)) {
                item = widget?.getFocus();
            }
            if (!isResponseVM(item) || !widget) {
                return;
            }
            const request = chatService.getSession(item.sessionId)?.getRequests().find(candidate => candidate.id === item.requestId);
            if (request) {
                await chatService.resendRequest(request, { noCommandDetection: false, attempt: request.attempt + 1, location: widget.location, implicitVariablesEnabled: true });
            }
        }
    });
    registerAction2(class RerunWithoutCommandDetectionAction extends Action2 {
        constructor() {
            super({
                id: 'workbench.action.chat.rerunWithoutCommandDetection',
                title: ( localize2WithPath(_moduleId, 7, "Rerun without Command Detection")),
                f1: false,
                category: CHAT_CATEGORY,
                icon: Codicon.refresh,
                precondition: ( (CONTEXT_CHAT_LOCATION.isEqualTo(ChatAgentLocation.Editor))),
                menu: {
                    when: CONTEXT_RESPONSE_DETECTED_AGENT_COMMAND,
                    id: rerunMenu,
                    group: 'navigation',
                    order: -1,
                }
            });
        }
        async run(accessor, ...args) {
            const chatWidgetService = accessor.get(IChatWidgetService);
            const chatService = accessor.get(IChatService);
            const widget = chatWidgetService.lastFocusedWidget;
            let item = args[0];
            if (!isResponseVM(item)) {
                item = widget?.getFocus();
            }
            if (!isResponseVM(item) || !widget) {
                return;
            }
            const request = chatService.getSession(item.sessionId)?.getRequests().find(candidate => candidate.id === item.requestId);
            if (request) {
                await chatService.resendRequest(request, { noCommandDetection: true, attempt: request.attempt, location: widget.location, implicitVariablesEnabled: true });
            }
        }
    });
}
function splitMarkdownAndCodeBlocks(markdown) {
    const lexer = new marked_1.Lexer();
    const tokens = lexer.lex(markdown);
    const splitContent = [];
    let markdownPart = '';
    tokens.forEach((token) => {
        if (token.type === 'code') {
            if (markdownPart.trim()) {
                splitContent.push({ type: 'markdown', content: markdownPart });
                markdownPart = '';
            }
            splitContent.push({
                type: 'code',
                language: token.lang || '',
                content: token.text,
            });
        }
        else {
            markdownPart += token.raw;
        }
    });
    if (markdownPart.trim()) {
        splitContent.push({ type: 'markdown', content: markdownPart });
    }
    return splitContent;
}

export { registerChatTitleActions };
